import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AlertCircle, ArrowLeft } from 'lucide-react';

import Button from 'components/_UI/Button';
import Fieldset from 'components/_UI/Fieldset';
import Loader from 'components/_UI/Loader';
import userApi from 'api/requests/user';
import fieldValidations from 'helpers/fieldValidations';
import masks from 'helpers/masks';
import colors from 'theme/colors';
import { FullyCentered } from 'styled/alignment/Center';

import {
    Actions,
    FieldRow,
    FieldStack,
    StepError,
    StepShell,
    StepSubtitle,
    StepTitle,
    SubmitButton,
} from '../partnerStyles';

/**
 * Passo 2 — quem responde pela conta e como ela é acessada.
 *
 * A senha ficava no passo do negócio, junto de razão social e termos de uso;
 * aqui ela fica com o resto da identidade, que é o assunto dela.
 */
export default function StepAccount({ defaultValues, onSubmit, onBack }) {
    const titleRef = useRef(null);
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [stepError, setStepError] = useState('');

    const {
        register,
        handleSubmit,
        getValues,
        setError,
        formState: { errors },
    } = useForm({
        mode: 'onTouched',
        defaultValues: {
            firstName: defaultValues?.firstName || '',
            lastName: defaultValues?.lastName || '',
            email: defaultValues?.email || '',
            birthDate: defaultValues?.birthDate || '',
            phone: defaultValues?.phone || '',
            document: defaultValues?.document || '',
            password: '',
            confirmPassword: '',
        },
    });

    useEffect(() => {
        titleRef.current?.focus();
    }, []);

    async function handleValid(values) {
        setStepError('');
        setCheckingEmail(true);

        let isAvailable = null;

        try {
            const { data } = await userApi.dataEnable({ email: values.email });
            isAvailable = Boolean(data?.email);
        } catch (error) {
            /* Falha de rede não é e-mail ocupado. A versão anterior tratava as duas
               como a mesma coisa e mandava a pessoa trocar de e-mail por causa de
               uma conexão instável. */
            isAvailable = null;
        } finally {
            setCheckingEmail(false);
        }

        if (isAvailable === null) {
            setStepError('Não conseguimos verificar o e-mail agora. Confira sua conexão e tente de novo.');
            return;
        }

        if (!isAvailable) {
            setError('email', {
                type: 'manual',
                message: 'Este e-mail já tem conta no Whizr. Use outro ou entre com ele.',
            }, { shouldFocus: true });
            return;
        }

        onSubmit(values);
    }

    return (
        <StepShell>
            <StepTitle ref={titleRef} tabIndex={-1}>
                Quem responde por essa conta?
            </StepTitle>
            <StepSubtitle>
                Precisa ser a pessoa titular do cadastro — é o nome que vai nos repasses.
            </StepSubtitle>

            <form onSubmit={handleSubmit(handleValid)} noValidate>
                <FieldStack>
                    <FieldRow>
                        <Fieldset
                            layout='squired'
                            label='Nome'
                            placeholder='Ana'
                            name='firstName'
                            register={register}
                            validations={fieldValidations.personName('nome')}
                            error={errors.firstName?.message}
                        />

                        <Fieldset
                            layout='squired'
                            label='Sobrenome'
                            placeholder='Souza'
                            name='lastName'
                            register={register}
                            validations={fieldValidations.personName('sobrenome')}
                            error={errors.lastName?.message}
                        />
                    </FieldRow>

                    <Fieldset
                        layout='squired'
                        label='E-mail'
                        placeholder='voce@exemplo.com'
                        name='email'
                        register={register}
                        validations={fieldValidations.email('e-mail')}
                        error={errors.email?.message}
                    />

                    <FieldRow>
                        <Fieldset
                            layout='squired'
                            label='Data de nascimento'
                            placeholder='00/00/0000'
                            mask={masks.date}
                            name='birthDate'
                            register={register}
                            validations={fieldValidations.birthdate('data de nascimento')}
                            error={errors.birthDate?.message}
                        />

                        <Fieldset
                            layout='squired'
                            label='Celular (WhatsApp)'
                            placeholder='(00) 00000-0000'
                            mask={masks.phone}
                            name='phone'
                            register={register}
                            validations={fieldValidations.phone('celular')}
                            error={errors.phone?.message}
                        />
                    </FieldRow>

                    <Fieldset
                        layout='squired'
                        label='CPF'
                        placeholder='000.000.000-00'
                        mask={masks.cpf}
                        name='document'
                        register={register}
                        validations={fieldValidations.cpf('CPF')}
                        error={errors.document?.message}
                    />

                    <Fieldset
                        layout='squired'
                        label='Senha'
                        placeholder='Crie uma senha'
                        inputType='password'
                        badgeIconPassword
                        badgeIconColorPassword={colors.night}
                        badgeIconPositionPassword='right'
                        name='password'
                        register={register}
                        validations={fieldValidations.password('senha')}
                        error={errors.password?.message}
                    />

                    <Fieldset
                        layout='squired'
                        label='Confirme a senha'
                        placeholder='Repita a senha'
                        inputType='password'
                        badgeIconPassword
                        badgeIconColorPassword={colors.night}
                        badgeIconPositionPassword='right'
                        name='confirmPassword'
                        register={register}
                        validations={{
                            required: 'Confirme a senha para continuar',
                            /* Só a comparação. Repetir aqui o padrão da senha fazia a
                               confirmação exibir a lista de critérios quando o real
                               problema era as duas estarem diferentes. */
                            validate: (value) => (
                                value === getValues('password') || 'As senhas não coincidem'
                            ),
                        }}
                        error={errors.confirmPassword?.message}
                    />
                </FieldStack>

                {stepError && (
                    <StepError role='alert'>
                        <AlertCircle size={15} aria-hidden='true' />
                        {stepError}
                    </StepError>
                )}

                <Actions>
                    {checkingEmail ? (
                        <FullyCentered>
                            <Loader />
                        </FullyCentered>
                    ) : (
                        <>
                            <SubmitButton>
                                Continuar
                            </SubmitButton>

                            <Button category='minimalDark' onClick={onBack}>
                                <ArrowLeft size={20} aria-hidden='true' /> &nbsp; Voltar
                            </Button>
                        </>
                    )}
                </Actions>
            </form>
        </StepShell>
    );
}
